import { useCallback } from 'react';
import { ProCard } from '@ant-design/pro-components';
import { Avatar, Form, Input, Select, Space, Typography } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import Permission from '@/components/permission';
import { resolveAssetUrl } from '@/pages/drama-review/utils';
import UgcReportListPage from '@/pages/ugc-reports/components/UgcReportListPage';
import { REPORT_PERMISSION, USER_REPORT_TYPE_OPTIONS } from '@/pages/ugc-reports/constants';
import { buildTrailingReportColumns } from '@/pages/ugc-reports/hooks/useTrailingReportColumns';
import { formatReportTypeLabel, getTargetString } from '@/pages/ugc-reports/utils';

import type { ProColumns } from '@ant-design/pro-components';
import type { UgcReportTicket } from '@/services/ugc-reports';

const numericInputProps = {
    getValueFromEvent: (e: { target?: { value?: string } }) =>
        String(e?.target?.value ?? '').replace(/\D/g, ''),
};

const PageUgcUserReports: React.FC = () => {
    const { formatMessage, messages } = useIntl();
    const navigate = useNavigate();

    const reportTypeOptions = USER_REPORT_TYPE_OPTIONS.map((item) => ({
        label: formatReportTypeLabel(
            item.code,
            'user',
            formatMessage,
            messages as Record<string, unknown>,
        ),
        value: item.code,
    }));

    const buildColumns = useCallback(
        ({
            expandedRowKeys,
            toggleExpand,
        }: {
            expandedRowKeys: string[];
            toggleExpand: (record: UgcReportTicket) => void;
        }) => {
            const trailing = buildTrailingReportColumns({
                dictionary: 'user',
                expandedRowKeys,
                toggleExpand,
                navigate,
            });
            return [
                {
                    title: <FormattedMessage id="ugcReports.columns.reportedUserAccount" />,
                    width: 300,
                    render: (_: unknown, record: UgcReportTicket) => {
                        const target = record.target;
                        const nickname =
                            getTargetString(target, 'nickname') || getTargetString(target, 'name');
                        const avatar = getTargetString(target, 'avatar_url');
                        return (
                            <Space align="start">
                                <Avatar src={avatar ? resolveAssetUrl(avatar) : undefined}>
                                    {nickname?.charAt(0) || '?'}
                                </Avatar>
                                <Space direction="vertical" size={2}>
                                    <Typography.Text strong>{nickname || '-'}</Typography.Text>
                                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                        <FormattedMessage id="ugcReports.columns.uidLabel" />{' '}
                                        <Typography.Text copyable={{ text: record.target_id }}>
                                            {record.target_id}
                                        </Typography.Text>
                                    </Typography.Text>
                                </Space>
                            </Space>
                        );
                    },
                },
                trailing.reasonColumn,
                trailing.timeRangeColumn,
                trailing.reportCountColumn,
                trailing.actionColumn,
            ] as ProColumns<UgcReportTicket>[];
        },
        [navigate],
    );

    const searchFields = (
        <>
            <Form.Item name="report_type_code" style={{ marginBottom: 0 }}>
                <Select
                    allowClear
                    options={reportTypeOptions}
                    placeholder={formatMessage({ id: 'ugcReports.search.reportType' })}
                    style={{ width: 180 }}
                />
            </Form.Item>
            <Form.Item name="reported_user_id" style={{ marginBottom: 0 }} {...numericInputProps}>
                <Input
                    allowClear
                    inputMode="numeric"
                    placeholder={formatMessage({ id: 'ugcReports.search.reportedUserId' })}
                    style={{ width: 200 }}
                />
            </Form.Item>
            <Form.Item name="reported_user_nickname" style={{ marginBottom: 0 }}>
                <Input
                    allowClear
                    placeholder={formatMessage({
                        id: 'ugcReports.search.reportedUserNickname',
                    })}
                    style={{ width: 200 }}
                />
            </Form.Item>
            <Form.Item name="reporter_user_id" style={{ marginBottom: 0 }} {...numericInputProps}>
                <Input
                    allowClear
                    inputMode="numeric"
                    placeholder={formatMessage({ id: 'ugcReports.search.reporterId' })}
                    style={{ width: 200 }}
                />
            </Form.Item>
            <Form.Item name="reporter_nickname" style={{ marginBottom: 0 }}>
                <Input
                    allowClear
                    placeholder={formatMessage({ id: 'ugcReports.search.reporterNickname' })}
                    style={{ width: 200 }}
                />
            </Form.Item>
        </>
    );

    return (
        <Permission permission={REPORT_PERMISSION}>
            <ProCard>
                <UgcReportListPage
                    targetType="USER"
                    buildColumns={buildColumns}
                    searchFields={searchFields}
                    dictionary="user"
                    scrollX={1300}
                    withTimeFilter={false}
                />
            </ProCard>
        </Permission>
    );
};

export default PageUgcUserReports;
